const DATABASE_NAME = "node-client-identity";
const DATABASE_VERSION = 1;
const OBJECT_STORE = "identity";
const KEY_MATERIAL_KEY = "key-material";
const DELEGATION_KEY = "delegation";
const CONNECTION_METADATA_KEY = "connection-metadata";
const LIFECYCLE_KEY = "lifecycle";
export class IndexedDbIdentityStore {
    factory;
    constructor(factory = globalThis.indexedDB) {
        this.factory = factory;
    }
    async readKeyMaterial() {
        return await this.#read(KEY_MATERIAL_KEY);
    }
    async writeKeyMaterial(value) {
        const database = await openIdentityDatabase(this.factory);
        try {
            const transaction = database.transaction(OBJECT_STORE, "readwrite");
            const store = transaction.objectStore(OBJECT_STORE);
            const current = await requestResult(store.get(LIFECYCLE_KEY));
            store.put(value, KEY_MATERIAL_KEY);
            store.put({
                generation: (current?.generation ?? 0) + 1,
                state: "active",
            }, LIFECYCLE_KEY);
            await transactionDone(transaction);
        }
        finally {
            database.close();
        }
    }
    async readDelegation() {
        return await this.#read(DELEGATION_KEY);
    }
    async writeDelegation(value) {
        await this.#write(DELEGATION_KEY, value);
    }
    async readConnectionMetadata() {
        return await this.#read(CONNECTION_METADATA_KEY);
    }
    async writeConnectionMetadata(value) {
        await this.#write(CONNECTION_METADATA_KEY, value);
    }
    async readLifecycle() {
        return await this.#read(LIFECYCLE_KEY);
    }
    async clearDelegation() {
        await this.#delete(DELEGATION_KEY);
    }
    async replaceRevoked(expectedGeneration, value) {
        const database = await openIdentityDatabase(this.factory);
        try {
            const transaction = database.transaction(OBJECT_STORE, "readwrite");
            const store = transaction.objectStore(OBJECT_STORE);
            const current = await requestResult(store.get(LIFECYCLE_KEY));
            if (current?.state !== "revoked" || current.generation !== expectedGeneration) {
                await transactionDone(transaction);
                return false;
            }
            store.put(value, KEY_MATERIAL_KEY);
            store.put({
                generation: current.generation + 1,
                state: "active",
            }, LIFECYCLE_KEY);
            await transactionDone(transaction);
            return true;
        }
        finally {
            database.close();
        }
    }
    async markRevoked() {
        const database = await openIdentityDatabase(this.factory);
        try {
            const transaction = database.transaction(OBJECT_STORE, "readwrite");
            const store = transaction.objectStore(OBJECT_STORE);
            const current = await requestResult(store.get(LIFECYCLE_KEY));
            store.clear();
            store.put({
                generation: (current?.generation ?? 0) + 1,
                state: "revoked",
            }, LIFECYCLE_KEY);
            await transactionDone(transaction);
        }
        finally {
            database.close();
        }
    }
    async purgeCorrupt() {
        const database = await openIdentityDatabase(this.factory);
        try {
            const transaction = database.transaction(OBJECT_STORE, "readwrite");
            transaction.objectStore(OBJECT_STORE).clear();
            await transactionDone(transaction);
        }
        finally {
            database.close();
        }
    }
    async #read(key) {
        const database = await openIdentityDatabase(this.factory);
        try {
            const transaction = database.transaction(OBJECT_STORE, "readonly");
            const request = transaction.objectStore(OBJECT_STORE).get(key);
            const value = await requestResult(request);
            await transactionDone(transaction);
            return value;
        }
        finally {
            database.close();
        }
    }
    async #write(key, value) {
        const database = await openIdentityDatabase(this.factory);
        try {
            const transaction = database.transaction(OBJECT_STORE, "readwrite");
            transaction.objectStore(OBJECT_STORE).put(value, key);
            await transactionDone(transaction);
        }
        finally {
            database.close();
        }
    }
    async #delete(key) {
        const database = await openIdentityDatabase(this.factory);
        try {
            const transaction = database.transaction(OBJECT_STORE, "readwrite");
            transaction.objectStore(OBJECT_STORE).delete(key);
            await transactionDone(transaction);
        }
        finally {
            database.close();
        }
    }
}
function openIdentityDatabase(factory) {
    return new Promise((resolve, reject) => {
        const request = factory.open(DATABASE_NAME, DATABASE_VERSION);
        request.addEventListener("upgradeneeded", () => {
            if (!request.result.objectStoreNames.contains(OBJECT_STORE)) {
                request.result.createObjectStore(OBJECT_STORE);
            }
        }, { once: true });
        request.addEventListener("success", () => resolve(request.result), { once: true });
        request.addEventListener("error", () => reject(new Error("Identity storage unavailable")), { once: true });
        request.addEventListener("blocked", () => reject(new Error("Identity storage unavailable")), { once: true });
    });
}
function requestResult(request) {
    return new Promise((resolve, reject) => {
        request.addEventListener("success", () => resolve(request.result), { once: true });
        request.addEventListener("error", () => reject(new Error("Identity storage operation failed")), { once: true });
    });
}
function transactionDone(transaction) {
    return new Promise((resolve, reject) => {
        transaction.addEventListener("complete", () => resolve(), { once: true });
        transaction.addEventListener("abort", () => reject(new Error("Identity storage operation failed")), { once: true });
        transaction.addEventListener("error", () => reject(new Error("Identity storage operation failed")), { once: true });
    });
}
//# sourceMappingURL=identity-store.js.map